/**
 * Staffelstand — live bijwerken. Hoort bij snippets/zorenza-staffel.liquid.
 *
 * De server tekent de beginstand. Dit script rekent dezelfde stand opnieuw uit
 * zodra de winkelwagen verandert (toevoegen, lade, Versandschutz), zodat de
 * klant niet hoeft te verversen. De regels zijn gelijk aan die in Liquid:
 *
 *   - telt artikelen in de winkelwagen, zonder diensten (Versandschutz)
 *   - volgende trede = eerste drempel boven het aantal
 *   - bereikt = hoogste drempel op of onder het aantal
 *
 * Wijzig je hier iets, wijzig het dan ook in de snippet.
 */

import { StandardEvents } from '@shopify/events';

const TAG = 'zorenza-staffel';

class ZorenzaStaffel extends HTMLElement {
  connectedCallback() {
    this.tiers = (this.dataset.tiers || '')
      .split(',')
      .filter(Boolean)
      .map((t) => {
        const [qty, pct] = t.split(':').map(Number);
        return { qty, pct };
      })
      .sort((a, b) => a.qty - b.qty);
    this.services = (this.dataset.services || '').split(',').map((s) => s.trim()).filter(Boolean);
  }

  /** @param {number} count */
  render(count) {
    if (!this.tiers.length) return;
    this.dataset.count = String(count);

    const next = this.tiers.find((t) => t.qty > count);
    const reached = [...this.tiers].reverse().find((t) => t.qty <= count);
    const max = this.tiers[this.tiers.length - 1];
    const u = (v) => `<u>${v}</u>`;

    this.classList.toggle('is-max', !next);

    const head = this.querySelector('[data-head]');
    if (head && this.dataset.nextMap) {
      // Lade (correctie Z-02): zinnen per aantal uit de taalbestanden, geen <u>.
      let map = {};
      try {
        map = JSON.parse(this.dataset.nextMap);
      } catch (e) {}
      head.innerHTML = next
        ? String(map[next.qty - count] || '').replace('{pct}', next.pct)
        : (this.dataset.max || '').replace('{pct}', max.pct);
    } else if (head) {
      head.innerHTML = next
        ? (this.dataset.next || '').replace('{n}', u(next.qty - count)).replace('{pct}', u(next.pct))
        : (this.dataset.max || '').replace('{pct}', u(max.pct));
    }

    const target = next ? next.qty : max.qty;
    const span = Math.max(max.qty - 1, 1);
    // Lade: aantal / hoogste drempel (1 van 4 = 25 %). Elders: 1 artikel = begin.
    const raw = this.dataset.nextMap ? (count * 100) / max.qty : ((count - 1) * 100) / span;
    const fill = Math.min(Math.max(raw, 0), 100);

    const bar = this.querySelector('[data-fill]');
    if (bar) bar.style.width = `${fill}%`;

    for (const el of this.querySelectorAll('.z-staffel__track s[data-qty]')) {
      el.classList.toggle('is-done', Number(el.dataset.qty) <= count);
    }
    for (const el of this.querySelectorAll('.z-staffel__steps [data-qty]')) {
      el.classList.toggle('is-on', reached ? Number(el.dataset.qty) === reached.qty : false);
    }
    for (const el of this.querySelectorAll('.z-staffel__step[data-qty]')) {
      el.classList.toggle('is-on', Number(el.dataset.qty) === target);
    }
  }

  async refresh() {
    // Productpagina: de balk volgt de gekozen rij, niet de winkelwagen.
    if (this.dataset.bron === 'keuze') return;
    try {
      // routes.cart_url, niet root_url: die laatste is in een markt "/de-de"
      // zónder slash, en dan wordt het "/de-decart.js" (404).
      const cartUrl = this.dataset.cartUrl || '/cart';
      const cart = await fetch(`${cartUrl}.js`).then((r) => r.json());
      const count = (cart.items || [])
        .filter((item) => !this.services.includes(item.handle))
        .reduce((sum, item) => sum + item.quantity, 0);
      this.render(count);
    } catch (error) {
      console.error('[zorenza-staffel]', error);
    }
  }
}

if (!customElements.get(TAG)) {
  customElements.define(TAG, ZorenzaStaffel);

  // Productpagina (16 sep): andere rij in "Menge wählen" → balk eronder bijwerken.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('mv-tier__input')) return;
    const balk = input.closest('mivoza-tiers')?.querySelector(`${TAG}[data-bron="keuze"]`);
    if (balk instanceof ZorenzaStaffel) balk.render(Number(input.value) || 1);
  });

  // Eén luisteraar voor alle exemplaren op de pagina. Een timeout en geen
  // requestAnimationFrame: dat laatste pauzeert in een tabblad buiten beeld.
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const done = () =>
      setTimeout(() => {
        for (const el of document.querySelectorAll(TAG)) {
          if (el instanceof ZorenzaStaffel) el.refresh();
        }
      }, 150);
    if (event.promise) event.promise.then(done, done);
    else done();
  });
}
